import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { VideoCamera, MagnifyingGlass, ArrowsCounterClockwise, ShieldStar, ShieldCheck, Plus, CheckCircle, XCircle, Envelope, Broom, ClockCounterClockwise } from "@phosphor-icons/react";
import AlarmAddendumModal from "@/pages/AlarmAddendumModal";
import EmailAuditModal from "@/pages/EmailAuditModal";
import CameraHistoryModal from "@/components/CameraHistoryModal";
import BulkRingSyncModal from "@/components/BulkRingSyncModal";

const NON_DEPLOYED = new Set(["in_stock", "retired", "offline", "stolen"]);

// Security Fleet — cameras + Ring alarms in one screen with inline edits.
export default function AdminCameras() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [statusOptions, setStatusOptions] = useState({ camera_statuses: [], alarm_statuses: [] });
  const [landlords, setLandlords] = useState([]);
  const [storageLocs, setStorageLocs] = useState([]);
  const [addendums, setAddendums] = useState([]);
  const [q, setQ] = useState("");
  const [kindF, setKindF] = useState("all");
  const [ownerF, setOwnerF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [landlordF, setLandlordF] = useState("all");
  const [savingId, setSavingId] = useState(null);
  const [showAddendumModal, setShowAddendumModal] = useState(false);
  const [addendumPrefill, setAddendumPrefill] = useState({});
  const [showLocMgr, setShowLocMgr] = useState(false);
  const [showEmailAudit, setShowEmailAudit] = useState(false);
  const [newLoc, setNewLoc] = useState("");
  const [historyCam, setHistoryCam] = useState(null);
  const [bulkRingOpen, setBulkRingOpen] = useState(false);

  const load = async () => {
    try {
      const [fleet, ll, locs, adds] = await Promise.all([
        api.get("/security-fleet"),
        api.get("/landlords"),
        api.get("/storage-locations"),
        api.get("/alarm-addendums"),
      ]);
      setRows(fleet.data?.rows || []);
      setSummary(fleet.data?.summary || null);
      setStatusOptions({
        camera_statuses: fleet.data?.camera_statuses || [],
        alarm_statuses:  fleet.data?.alarm_statuses  || [],
      });
      setLandlords(ll.data || []);
      setStorageLocs(locs.data || []);
      setAddendums(adds.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load security fleet");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (kindF !== "all" && r.kind !== kindF) return false;
      if (ownerF !== "all" && (r.owner || "") !== ownerF) return false;
      if (statusF !== "all" && (r.status || "") !== statusF) return false;
      if (landlordF !== "all") {
        if (landlordF === "__none__") { if (r.landlord_id) return false; }
        else if (r.landlord_id !== landlordF) return false;
      }
      if (!needle) return true;
      const hay = `${r.code || ""} ${r.address || ""} ${r.unit || ""} ${r.name || ""} ${r.landlord_name || ""} ${r.storage_location || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, kindF, ownerF, statusF, landlordF]);

  const allStatuses = useMemo(() => {
    const s = new Set([...(statusOptions.camera_statuses || []),
                       ...(statusOptions.alarm_statuses || [])]);
    return [...s];
  }, [statusOptions]);

  const patchRow = async (row, field, value) => {
    setSavingId(row.id);
    try {
      const path = row.kind === "camera"
        ? `/security-fleet/camera/${row.id}`
        : `/security-fleet/alarm/${row.id}`;
      await api.patch(path, { [field]: value });
      setRows(prev => prev.map(r => {
        if (r.id !== row.id) return r;
        const next = { ...r, [field]: value };
        if (field === "landlord_id") {
          const match = landlords.find(l => l.id === value);
          next.landlord_name = match ? match.name : (value === "" ? "" : r.landlord_name);
        }
        return next;
      }));
      toast.success(`${field.replace(/_/g," ")} updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
      load();
    } finally { setSavingId(null); }
  };

  const decideAddendum = async (a, decision) => {
    if (!window.confirm(`Mark this addendum as ${decision}?`)) return;
    try {
      await api.post(`/alarm-addendums/${a.id}/manual-decision`,
                     { decision, note: "manual mark from admin" });
      toast.success(decision === "approved" ? "Approved — job created" : "Declined");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Update failed")); }
  };

  const addLoc = async () => {
    if (!newLoc.trim()) return;
    try {
      await api.post("/storage-locations", { name: newLoc.trim() });
      setNewLoc("");
      const locs = await api.get("/storage-locations");
      setStorageLocs(locs.data || []);
      toast.success("Location added");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Add failed")); }
  };
  const removeLoc = async (id) => {
    if (id.startsWith("seed:")) return toast.error("Seed locations can't be deleted — add your own to override");
    try {
      await api.delete(`/storage-locations/${id}`);
      const locs = await api.get("/storage-locations");
      setStorageLocs(locs.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed")); }
  };

  const daysSince = (iso) => {
    if (!iso) return null;
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
    return d >= 0 ? d : null;
  };

  const activeAddendums = addendums.filter(a => a.status !== "approved" && a.status !== "declined");

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="admin-cameras-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">Security Fleet</div>
            <h1 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2">
              <VideoCamera weight="fill" size={26}/> Cameras + Alarms
            </h1>
            <div className="text-xs text-slate-500 mt-1">Every camera + Ring alarm in one table. Inline dropdowns for landlord, status, and storage. Draft owner-permission addendums to install new alarms.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setBulkRingOpen(true)} data-testid="bulk-ring-sync-btn"
              className="h-10 px-3 border-2 border-fuchsia-700 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
              📱 Bulk Ring Sync
            </button>
            <button onClick={() => { setAddendumPrefill({}); setShowAddendumModal(true); }} data-testid="new-addendum-btn"
              className="h-10 px-3 border-2 border-[#0f172a] bg-amber-400 hover:bg-amber-500 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
              <Plus size={14}/> Alarm Addendum
            </button>
            <button onClick={() => setShowEmailAudit(true)} data-testid="email-audit-btn"
              className="h-10 px-3 border-2 border-red-700 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
              <Broom size={14} weight="fill"/> Email Audit
            </button>
            <button onClick={() => setShowLocMgr(o => !o)} data-testid="manage-locs-btn"
              className="h-10 px-3 border-2 border-slate-800 bg-white hover:bg-slate-100 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
              {showLocMgr ? "Hide" : "Storage"} Locations
            </button>
            <button onClick={load} data-testid="reload-cameras-btn"
              className="h-10 px-3 border-2 border-slate-800 bg-white hover:bg-slate-100 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
              <ArrowsCounterClockwise size={14}/> Refresh
            </button>
          </div>
        </div>

        {/* Active Addendums panel */}
        {activeAddendums.length > 0 && (
          <div className="p-3 border-2 border-amber-500 bg-amber-50" data-testid="addendums-active-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 flex items-center gap-1">
                <Envelope size={12}/> Pending Owner Decision ({activeAddendums.length})
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {activeAddendums.slice(0, 6).map(a => (
                <div key={a.id} className="p-2 bg-white border border-amber-300 text-xs" data-testid={`addendum-row-${a.id}`}>
                  <div className="font-black text-[#0f172a] truncate">
                    {a.address}{a.unit ? ` · Unit ${a.unit}` : ""}
                  </div>
                  <div className="text-[11px] text-slate-600">
                    → {a.owner_email} · {a.scope?.sensors || 0} sens / {a.scope?.cameras || 0} cams
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1 items-center">
                    <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                      a.status === "draft" ? "bg-slate-500 text-white" :
                      a.status === "sent" ? "bg-amber-500 text-white" :
                      "bg-red-600 text-white"
                    }`}>{a.status.replace(/_/g," ")}</span>
                    <button onClick={() => decideAddendum(a, "approved")}
                      data-testid={`addendum-approve-${a.id}`}
                      className="ml-auto h-6 px-2 bg-emerald-600 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1 hover:bg-emerald-700">
                      <CheckCircle size={10}/> Mark Approved
                    </button>
                    <button onClick={() => decideAddendum(a, "declined")}
                      data-testid={`addendum-decline-${a.id}`}
                      className="h-6 px-2 bg-slate-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1 hover:bg-slate-800">
                      <XCircle size={10}/> Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Storage Location Manager */}
        {showLocMgr && (
          <div className="p-3 border-2 border-slate-500 bg-slate-50" data-testid="storage-loc-mgr">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Storage Locations</div>
            <div className="flex flex-wrap gap-1 mb-2">
              {storageLocs.map(l => (
                <div key={l.id} className={`inline-flex items-center gap-1 px-2 py-1 border-2 text-xs font-bold ${
                  l.id.startsWith("seed:") ? "border-slate-300 bg-white text-slate-600" : "border-slate-800 bg-white text-slate-900"
                }`}>
                  {l.name}
                  {!l.id.startsWith("seed:") && (
                    <button onClick={() => removeLoc(l.id)} className="text-red-600 font-black">×</button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newLoc} onChange={e => setNewLoc(e.target.value)}
                placeholder="Add new location (e.g. Storage Unit 3B)"
                data-testid="new-loc-input"
                className="flex-1 h-9 px-2 border-2 border-slate-300 text-xs"/>
              <button onClick={addLoc} data-testid="add-loc-btn"
                className="h-9 px-3 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest">Add</button>
            </div>
          </div>
        )}

        {/* Summary strip */}
        {summary && (
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2" data-testid="cameras-summary">
            <SummaryChip label="Total" value={summary.total} color="slate"/>
            <SummaryChip label="Cameras" value={summary.cameras} color="cyan"/>
            <SummaryChip label="Alarms" value={summary.alarms} color="amber"/>
            <SummaryChip label="Active" value={summary.active} color="emerald"/>
            <SummaryChip label="Stolen" value={summary.stolen} color="red"/>
            <SummaryChip label="Alarmed" value={summary.alarmed} color="emerald"/>
            <SummaryChip label="Unsecured" value={summary.unsecured} color="red"/>
          </div>
        )}

        {/* Filter bar */}
        <div className="p-3 bg-white border-2 border-slate-300 flex flex-wrap items-center gap-2" data-testid="cameras-filter-bar">
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input list="known-addresses" autoComplete="off" value={q} onChange={e => setQ(e.target.value)} placeholder="Search code / address / unit / landlord / storage"
              data-testid="cameras-search"
              className="w-full h-10 pl-7 pr-2 border-2 border-slate-300 text-sm"/>
          </div>
          <select value={kindF} onChange={e => setKindF(e.target.value)} data-testid="cameras-kind-filter"
            className="h-10 px-2 border-2 border-slate-300 text-xs font-bold uppercase tracking-wider">
            <option value="all">All kinds</option>
            <option value="camera">Cameras</option>
            <option value="alarm">Alarms</option>
          </select>
          <select value={landlordF} onChange={e => setLandlordF(e.target.value)} data-testid="cameras-landlord-filter"
            className="h-10 px-2 border-2 border-slate-300 text-xs font-bold uppercase tracking-wider">
            <option value="all">All landlords</option>
            <option value="__none__">— No landlord —</option>
            {landlords.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select value={ownerF} onChange={e => setOwnerF(e.target.value)} data-testid="cameras-owner-filter"
            className="h-10 px-2 border-2 border-slate-300 text-xs font-bold uppercase tracking-wider">
            <option value="all">All owners</option>
            <option value="procall">ProCall</option>
            <option value="raza">Raza</option>
          </select>
          <select value={statusF} onChange={e => setStatusF(e.target.value)} data-testid="cameras-status-filter"
            className="h-10 px-2 border-2 border-slate-300 text-xs font-bold uppercase tracking-wider">
            <option value="all">All statuses</option>
            {allStatuses.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            Showing {filtered.length}{rows && filtered.length !== rows.length ? ` / ${rows.length}` : ""}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-slate-300 overflow-x-auto">
          <table className="w-full text-sm" data-testid="cameras-table">
            <thead className="bg-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Deployed at</th>
                <th className="text-left px-3 py-2">Landlord</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Stored at</th>
                <th className="text-left px-3 py-2">Last update</th>
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-slate-500">Loading…</td></tr>
              )}
              {rows !== null && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-slate-500">No devices match the filter.</td></tr>
              )}
              {filtered.map(r => {
                const iso = r.last_deployed_at || r.installed_at || r.updated_at || "";
                const d = daysSince(iso);
                const stale = d !== null && d > 90 && r.kind === "camera";
                const saving = savingId === r.id;
                const kindOptions = r.kind === "camera"
                  ? (statusOptions.camera_statuses || [])
                  : (statusOptions.alarm_statuses  || []);
                const showStorage = r.kind === "camera" && NON_DEPLOYED.has(r.status);
                const noAlarm = r.kind === "camera" && !r.address; // no address = camera not deployed
                return (
                  <tr key={`${r.kind}-${r.id}`} data-testid={`cam-row-${r.code || r.id}`}
                    className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      {r.kind === "camera" ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-100 border border-cyan-500 text-cyan-800 text-[10px] font-black uppercase tracking-widest">
                          <VideoCamera size={10}/> Cam
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 border border-amber-500 text-amber-800 text-[10px] font-black uppercase tracking-widest">
                          <ShieldStar size={10}/> Alarm
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono font-black">
                      {r.kind === "camera" ? (
                        <input defaultValue={r.code || ""}
                          placeholder="ST001 · RH006 · PC012 …"
                          onBlur={e => {
                            const v = e.target.value.trim();
                            if (v !== (r.code || "")) patchRow(r, "system_code", v);
                          }}
                          data-testid={`row-code-${r.id}`}
                          className="w-24 h-7 px-1 border border-transparent hover:border-slate-300 focus:border-cyan-500 focus:bg-cyan-50 font-mono font-black text-sm"/>
                      ) : (r.code || "—")}
                      {r.owner && (
                        <div className={`mt-0.5 inline-block px-1 text-[9px] font-black uppercase tracking-widest ${
                          r.owner === "raza" ? "bg-amber-100 text-amber-800" :
                          r.owner === "procall" ? "bg-emerald-100 text-emerald-800" :
                          "bg-slate-100 text-slate-600"
                        }`}>{r.owner}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.kind === "camera" ? (
                        <>
                          <input list="known-addresses" autoComplete="off" defaultValue={r.address || ""}
                            placeholder="Move camera → paste address"
                            onBlur={e => {
                              const v = e.target.value.trim();
                              if (v !== (r.address || "")) patchRow(r, "address", v);
                            }}
                            data-testid={`row-address-${r.id}`}
                            className="w-full max-w-[240px] h-7 px-1 border border-transparent hover:border-slate-300 focus:border-cyan-500 focus:bg-cyan-50 text-xs font-bold"/>
                          <input defaultValue={r.unit || ""}
                            placeholder="Unit"
                            onBlur={e => {
                              const v = e.target.value.trim();
                              if (v !== (r.unit || "")) patchRow(r, "unit", v);
                            }}
                            data-testid={`row-unit-${r.id}`}
                            className="mt-1 w-20 h-6 px-1 border border-transparent hover:border-slate-300 focus:border-cyan-500 focus:bg-cyan-50 text-[10px]"/>
                        </>
                      ) : (
                        <>
                          <div className="font-bold truncate max-w-[240px]">{r.address || <span className="italic text-slate-400">— not deployed —</span>}</div>
                          {r.unit && <div className="text-[10px] text-slate-500">Unit {r.unit}</div>}
                        </>
                      )}
                      {r.kind === "alarm" && (r.sensor_count > 0 || r.camera_count > 0) && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {r.sensor_count > 0 && <>{r.sensor_count} sensor{r.sensor_count === 1 ? "" : "s"} </>}
                          {r.camera_count > 0 && <>· {r.camera_count} cam{r.camera_count === 1 ? "" : "s"}</>}
                        </div>
                      )}
                      {r.kind === "camera" && r.address && (
                        <button onClick={() => {
                          const ll = landlords.find(l => l.id === r.landlord_id);
                          setAddendumPrefill({
                            address: r.address,
                            unit: r.unit || "",
                            landlord_id: r.landlord_id || "",
                            owner_email: ll?.email || "",
                            owner_name:  ll?.name  || r.landlord_name || "",
                          });
                          setShowAddendumModal(true);
                        }}
                          data-testid={`row-draft-addendum-${r.id}`}
                          className="mt-1 text-[9px] font-black uppercase tracking-widest text-amber-700 hover:text-amber-900 inline-flex items-center gap-0.5">
                          <ShieldCheck size={10}/> Draft addendum
                        </button>
                      )}
                      {r.kind === "camera" && (
                        <button onClick={() => setHistoryCam(r)}
                          data-testid={`row-history-${r.id}`}
                          className="mt-1 ml-2 text-[9px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-0.5">
                          <ClockCounterClockwise size={10}/> Move history
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={r.landlord_id || ""}
                        disabled={saving}
                        onChange={e => patchRow(r, "landlord_id", e.target.value)}
                        data-testid={`row-landlord-${r.id}`}
                        className="w-full max-w-[180px] h-8 px-1 border-2 border-slate-300 bg-white text-xs disabled:opacity-50">
                        <option value="">— None —</option>
                        {landlords.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={r.status || ""}
                        disabled={saving}
                        onChange={e => patchRow(r, "status", e.target.value)}
                        data-testid={`row-status-${r.id}`}
                        className={`h-8 px-1 border-2 text-[10px] font-black uppercase tracking-widest disabled:opacity-50 ${
                          r.status === "stolen" || r.status === "vacant_unsecured" ? "bg-red-600 text-white border-red-700"
                          : r.status === "active" || r.status === "vacant_alarmed" ? "bg-emerald-500 text-white border-emerald-600"
                          : r.status === "occupied" ? "bg-slate-600 text-white border-slate-700"
                          : "bg-slate-100 text-slate-800 border-slate-300"
                        }`}>
                        {kindOptions.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {showStorage ? (
                        <StorageCell row={r} locs={storageLocs} saving={saving}
                          onChange={v => patchRow(r, "storage_location", v)}/>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {iso ? (
                        <>
                          <div>{new Date(iso).toLocaleDateString()}</div>
                          <div className={`text-[10px] ${stale ? "text-red-600 font-black" : "text-slate-500"}`}>
                            {d} day{d === 1 ? "" : "s"} ago{stale ? " · stale >90d" : ""}
                          </div>
                        </>
                      ) : (
                        <span className="italic text-slate-400">never</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAddendumModal && (
        <AlarmAddendumModal
          landlords={landlords}
          prefill={addendumPrefill}
          addressOptions={[...new Set((rows || []).map(r => r.address).filter(Boolean))].sort()}
          onClose={() => setShowAddendumModal(false)}
          onCreated={load}
        />
      )}
      {showEmailAudit && (
        <EmailAuditModal onClose={() => setShowEmailAudit(false)}/>
      )}
      <CameraHistoryModal open={!!historyCam} camera={historyCam}
        onClose={() => setHistoryCam(null)}/>
      <BulkRingSyncModal open={bulkRingOpen} onClose={() => setBulkRingOpen(false)}
        onApplied={load}/>
    </AdminShell>
  );
}

function StorageCell({ row, locs, saving, onChange }) {
  const [mode, setMode] = useState(row.storage_location && !locs.find(l => l.name === row.storage_location) ? "custom" : "preset");
  const [custom, setCustom] = useState(row.storage_location || "");
  const commit = () => {
    if (custom.trim() !== (row.storage_location || "")) onChange(custom.trim());
  };
  if (mode === "custom") {
    return (
      <div className="flex gap-1">
        <input value={custom} onChange={e => setCustom(e.target.value)} onBlur={commit}
          placeholder="Type location…"
          data-testid={`row-storage-custom-${row.id}`}
          disabled={saving}
          className="w-28 h-8 px-1 border-2 border-slate-300 text-xs disabled:opacity-50"/>
        <button onClick={() => setMode("preset")}
          className="h-8 px-1 text-[9px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800">
          ▾
        </button>
      </div>
    );
  }
  return (
    <select value={row.storage_location || ""}
      disabled={saving}
      onChange={e => {
        if (e.target.value === "__custom__") setMode("custom");
        else onChange(e.target.value);
      }}
      data-testid={`row-storage-${row.id}`}
      className="w-36 h-8 px-1 border-2 border-slate-300 text-xs disabled:opacity-50">
      <option value="">— Set location —</option>
      {locs.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
      <option value="__custom__">✎ Custom…</option>
    </select>
  );
}

function SummaryChip({ label, value, color }) {
  const map = {
    slate: "border-slate-500 text-slate-800",
    emerald: "border-emerald-500 text-emerald-800",
    amber: "border-amber-500 text-amber-800",
    cyan: "border-cyan-500 text-cyan-800",
    red: "border-red-500 text-red-800",
  };
  return (
    <div className={`p-2 bg-white border-2 ${map[color] || map.slate}`} data-testid={`summary-${label.toLowerCase()}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display font-black text-xl">{value}</div>
    </div>
  );
}
